/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { getRichTextHtml, ICoreContext, ITelemetry } from '@msdyn365-commerce/core';
import { AttributeValue } from '@msdyn365-commerce/retail-proxy';
import { ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import React from 'react';

/**
 * Represents resources for the component.
 */
export interface IProductSpecificationResources {
    productComparisonProductItemSpecificationTrue: string;
    productComparisonProductItemSpecificationFalse: string;
}

/**
 * Represent configuration of the component.
 */
export interface IProductSpecificationProps {
    telemetry: ITelemetry;
    specification?: AttributeValue;
    context: ICoreContext;

    resources: IProductSpecificationResources;
}

/**
 * Represents the product specification values that comes from the server.
 */
export enum SpecificationDataTypeValues {
    none = 0,
    currency = 1,
    dateTime = 2,
    decimal = 3,
    integer = 4,
    text = 5,
    boolean = 6,
    video = 40,
    image = 41
}

/**
 * Represents the component which renders product comparison table.
 */
export class ProductSpecification extends React.PureComponent<IProductSpecificationProps> {
    private static _renderCurrencyCell(product: AttributeValue): React.ReactNode | null {
        if (ObjectExtensions.isNullOrUndefined(product.CurrencyValue)) {
            return null;
        }

        return (
            <span>
                {product.CurrencyCode}
                {product.CurrencyValue}
            </span>
        );
    }

    private static _renderDateTimeCell(product: AttributeValue): React.ReactNode | null {
        if (ObjectExtensions.isFunction(product.DateTimeOffsetValue?.toUTCString)) {
            return null;
        }

        return <span>{product.DateTimeOffsetValue!.toUTCString()}</span>;
    }

    private static _renderIntegerCell(product: AttributeValue): React.ReactNode | null {
        if (ObjectExtensions.isNullOrUndefined(product.IntegerValue)) {
            return null;
        }

        return <span>{product.IntegerValue}</span>;
    }

    private static _renderDecimalCell(product: AttributeValue): React.ReactNode | null {
        if (ObjectExtensions.isNullOrUndefined(product.FloatValue)) {
            return null;
        }

        return <span>{product.FloatValue}</span>;
    }

    private static _renderTextCell(product: AttributeValue): React.ReactNode | null {
        if (ObjectExtensions.isNullOrUndefined(product.TextValue)) {
            return null;
        }

        // eslint-disable-next-line react/no-danger -- Implementation is a copy.
        return <span dangerouslySetInnerHTML={getRichTextHtml(product.TextValue)} />;
    }

    public constructor(props: IProductSpecificationProps) {
        super(props);
    }

    public render(): React.ReactNode | null {
        const specification = this.props.specification;
        if (ObjectExtensions.isNullOrUndefined(specification)) {
            return null;
        }

        switch (specification.DataTypeValue) {
            case SpecificationDataTypeValues.none:
                // Return span here instead of null so the row still shows
                return <span />;
            case SpecificationDataTypeValues.currency:
                return ProductSpecification._renderCurrencyCell(specification);
            case SpecificationDataTypeValues.dateTime:
                return ProductSpecification._renderDateTimeCell(specification);
            case SpecificationDataTypeValues.decimal:
                return ProductSpecification._renderDecimalCell(specification);
            case SpecificationDataTypeValues.integer:
                return ProductSpecification._renderIntegerCell(specification);
            case SpecificationDataTypeValues.text:
                return ProductSpecification._renderTextCell(specification);
            case SpecificationDataTypeValues.boolean:
                return this._renderTrueFalseCell(specification);
            case SpecificationDataTypeValues.video:
                this.props.telemetry.warning('Got video specification type, not supported yet so skipping line');
                return null;
            case SpecificationDataTypeValues.image:
                this.props.telemetry.warning('Got image specification type, not supported yet so skipping line');
                return null;
            default:
                this.props.telemetry.warning(`Got unknown specification type ${specification.DataTypeValue ?? 'null'} so skipping line`);
                return null;
        }
    }

    private _renderTrueFalseCell(product: AttributeValue): React.ReactNode | null {
        if (product.BooleanValue) {
            return <span>{this.props.resources.productComparisonProductItemSpecificationTrue}</span>;
        }
        return <span>{this.props.resources.productComparisonProductItemSpecificationFalse}</span>;
    }
}
